import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { HelpCircle } from "lucide-react";

import { Input, Label, Button } from "components/ui";
import { AddCmsAction, UpdateCmsAction } from "store/middlewares/cms";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { SubmitLoaderButton } from "components/atoms";
import { closeAdminModal } from "store/slices/common/adminModal.slice";

const removeImages = (node, delta) => {
  const ops = delta.ops.filter((op) => {
    if (op.insert && typeof op.insert === "object" && op.insert.image) {
      return false;
    }
    return true;
  });
  delta.ops = ops;
  return delta;
};

const modules = {
  clipboard: {
    matchers: [["img", removeImages]],
  },
};

function ModalEditCommonIssue({ row }) {
  const dispatch = useDispatch();

  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [formError, setFormError] = useState(false);

  useEffect(() => {
    if (row) {
      setContent(row?.content);
      setTitle(row?.title);
    }
  }, [row]);

  useEffect(() => {
    const addTooltips = () => {
      const toolbar = document.querySelector(".quill-editor .ql-toolbar");
      if (!toolbar) return;

      const tooltips = {
        ".ql-bold": "Bold",
        ".ql-italic": "Italic",
        ".ql-underline": "Underline",
        ".ql-strike": "Strikethrough",
        ".ql-blockquote": "Blockquote",
        ".ql-code-block": "Code Block",
        ".ql-link": "Insert Link",
        ".ql-clean": "Clear Formatting",
      };

      Object.entries(tooltips).forEach(([selector, tooltip]) => {
        toolbar.querySelectorAll(selector).forEach((element) => {
          element.setAttribute("data-tooltip", tooltip);
        });
      });

      toolbar.querySelectorAll(".ql-list").forEach((element) => {
        if (element.value === "ordered") {
          element.setAttribute("data-tooltip", "Numbered List");
        }
        if (element.value === "bullet") {
          element.setAttribute("data-tooltip", "Bulleted List");
        }
      });
    };

    addTooltips();
    const observer = new MutationObserver(addTooltips);
    const toolbar = document.querySelector(".quill-editor .ql-toolbar");
    if (toolbar) {
      observer.observe(toolbar, {
        childList: true,
        subtree: true,
      });
    }
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (values) => {
    const updateValue = {
      ...values,
      title: values.title.trim(),
      content: values.content.trim(),
      onSuccess: () => formik.setSubmitting(false),
      onError: () => setFormError(true),
    };

    if (row) {
      dispatch(
        UpdateCmsAction({
          ...updateValue,
          type: "issue",
          id: row?.id,
        }),
      );
    } else {
      dispatch(
        AddCmsAction({
          ...updateValue,
          type: "issue",
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      title,
      content,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required("Title is required"),
      content: Yup.string().required("Description is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-6">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <HelpCircle className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit issue" : "Add issue"}
        </h2>
      </div>

      {/* Inputs (Stacked label + input) */}
      <div className="grid gap-5">
        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <Label>Title</Label>
          <Input
            type="text"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.title && formik.touched.title}
            errorMsg={formik.errors.title}
            isSpace={true}
            name="title"
            placeholder="Enter title"
            className="w-full"
            variant="lg"
          />
        </div>

        {/* Description (ReactQuill) */}
        <div className="flex flex-col gap-1.5">
          <Label>Description</Label>
          <div className="w-full">
            <ReactQuill
              theme="snow"
              modules={modules}
              value={formik.values.content}
              onBlur={() =>
                formik.handleBlur({
                  target: {
                    name: "content",
                  },
                })
              }
              onChange={(newContent) => {
                const plainText = newContent.replace(/<[^>]+>/g, "").trim();
                formik.setFieldValue("content", plainText ? newContent : "");
              }}
              // Removed ALL borders and rounded corners
              className="quill-editor"
            />
          </div>
          <div className="py-1">
            {formik.errors.content && formik.touched.content && (
              <span className="text-red-500 text-sm">
                {formik.errors.content}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditCommonIssue;